/* eslint-disable @typescript-eslint/no-explicit-any */
import { CHANNEL_SCALES, FIELD_OPTS, AGGREGATION_TYPES } from '@carto/kepler.gl/constants'
import {
  getColumnNameFromGeoColumn,
  getgeoColumnFromTilesetDataset,
  getSchemeFromTilesetDataset,
  getProviderFromDataset,
  getAggregationResLevelFromTilesetDataset,
  getDataResolutionFromTilesetDataset,
  getTilejsonFromTilesetDataset
} from 'features/builder/state/builderSliceUtils'
import TileLayer, { tilesetVisConfigs } from './TileLayer'
import {
  KeplerAggregationType,
  KeplerLayer,
  KeplerLayerConfig,
  KeplerLayerMeta,
  KeplerTable
} from 'features/builder/types'
import { createNewLayerColorGenerator } from './colorUtils'

const UNSUPPORTED_AGGREGATIONS: KeplerAggregationType[] = [AGGREGATION_TYPES.countUnique, AGGREGATION_TYPES.median]

export const spatialIndexVisConfigs = {
  ...tilesetVisConfigs,
  colorAggregation: 'colorAggregation',
  heightAggregation: 'heightAggregation',
  strokeColorAggregation: {
    type: 'string',
    defaultValue: AGGREGATION_TYPES.average
  }
}

export default class SpatialIndexLayer extends TileLayer {
  declare meta: KeplerLayerMeta & {
    spatialIndexIdName: string
  }

  constructor(props = {}) {
    super(props)
    this.registerVisConfig(spatialIndexVisConfigs)
  }

  get canDropFeatures() {
    return false
  }

  get visualChannels(): any {
    const visualChannels = super.visualChannels

    return {
      ...visualChannels,
      color: {
        ...visualChannels.color,
        aggregation: 'colorAggregation'
      },
      strokeColor: {
        ...visualChannels.strokeColor,
        aggregation: 'strokeColorAggregation'
      },
      size: {
        ...visualChannels.size,
        aggregation: 'sizeAggregation'
      },
      height: {
        ...visualChannels.height,
        aggregation: 'heightAggregation'
      }
    }
  }

  get useOpacityPerChannel() {
    return true
  }

  getHoverData(object: any) {
    if (!object) {
      return null
    }

    return {
      ...object.properties,
      // TODO: what is this about?
      ...(this.meta.spatialIndexIdName && { [this.meta.spatialIndexIdName]: object.id })
    }
  }

  formatLayerData(datasets: any, oldLayerData: any) {
    const dataset = datasets[this.config.dataId]
    if (!this.neededAttributesAreAvailable(dataset)) {
      return oldLayerData
    }

    const result = super.formatLayerData(datasets, oldLayerData)
    const aggregationResLevel = getAggregationResLevelFromTilesetDataset(dataset)
    const dataResolution = getDataResolutionFromTilesetDataset(dataset)
    const geoColumn = getgeoColumnFromTilesetDataset(dataset)
    const metadata = {
      provider: getProviderFromDataset(dataset),
      spatialIndexIdName: geoColumn ? getColumnNameFromGeoColumn(geoColumn) : getSchemeFromTilesetDataset(dataset),
      ...(typeof dataResolution === 'number' ? { dataResolution } : {})
    }

    this.updateMeta(metadata)

    return {
      ...result,
      aggregationResLevel,
      data: getTilejsonFromTilesetDataset(dataset)
    }
  }

  validateVisualChannel(channel) {
    // field type decides aggregation type decides scale type
    this.validateFieldType(channel)
    this.validateAggregationType(channel)
    this.validateScale(channel)
  }

  /**
   * Validate aggregation type based on selected field
   */
  validateAggregationType(channel) {
    const visualChannel = this.visualChannels[channel]
    const { field, aggregation } = visualChannel
    const aggregationOptions = this.getAggregationOptions(channel)

    if (!aggregation) {
      return
    }

    if (!aggregationOptions.length) {
      // if field cannot be aggregated, set field to null
      this.updateLayerConfig({ [field]: null })
    } else if (!aggregationOptions.includes(this.config.visConfig[aggregation])) {
      // current aggregation type is not supported by this field
      // set aggregation to the first supported option
      this.updateLayerVisConfig({ [aggregation]: aggregationOptions[0] })
    }
  }

  getScaleOptions(channel) {
    const visualChannel = this.visualChannels[channel]
    const { field, aggregation, channelScaleType } = visualChannel
    const aggregationType = this.config.visConfig[aggregation]
    const configField = this.config[field]
    const channelScale = channelScaleType === CHANNEL_SCALES.color ? CHANNEL_SCALES.colorAggr : CHANNEL_SCALES.sizeAggr
    return configField ? FIELD_OPTS[configField.type].scale[channelScale][aggregationType] : []
  }

  getAggregationOptions(channel) {
    const visualChannel = this.visualChannels[channel]
    const { field, channelScaleType } = visualChannel
    const configField = this.config[field]
    const channelScale = channelScaleType === CHANNEL_SCALES.color ? CHANNEL_SCALES.colorAggr : CHANNEL_SCALES.sizeAggr
    return Object.keys(configField ? FIELD_OPTS[configField.type].scale[channelScale] : '').filter(
      (aggregation) => !UNSUPPORTED_AGGREGATIONS.includes(aggregation as KeplerAggregationType)
    )
  }

  getNewLayerConfig(dataset: KeplerTable, otherLayers: KeplerLayer[]): Partial<KeplerLayerConfig> {
    const colorGenerator = createNewLayerColorGenerator(
      otherLayers.filter((layer) => layer !== (this as unknown as KeplerLayer))
    )
    const color = colorGenerator()
    const strokeColor = colorGenerator()
    return {
      color,
      visConfig: {
        ...this.config.visConfig,
        filled: true,
        stroked: true,
        enable3d: false,
        strokeColor,
        initialStrokeColor: strokeColor
      }
    }
  }
}
